import { useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import {
  Sparkles, ImageIcon, ShieldCheck, Cpu, X, Calendar, Clock,
  Maximize2, RefreshCw, ExternalLink, Layers, Box, Share2,
  CheckCircle2, Trash2, Wand2, GitBranch, GitCompare, Archive, ArchiveRestore,
  ChevronRight, ChevronDown, Building2, Home, Layers3,
  Hourglass, Eye, Anchor, Star, ShieldX, Scaling,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { HudCard, HudButton, HudBadge } from '../hud';
import RenderShareDialog from './RenderShareDialog';
import VariationDialog from './VariationDialog';
import BeforeAfterCompareDialog from './BeforeAfterCompareDialog';
import GalleryFocusView from './GalleryFocusView';
import { MaisonStackButton } from '../3d/MaisonRenderStack3D';
import StageCompareDialog, { stageOf } from './StageCompareDialog';
import StageWizard from './StageWizard';
import { emitRefsChanged, useRefsChanged } from '../../lib/generationEvents';
import AnnotationDialog from './AnnotationDialog';
import UpscaleDialog from './UpscaleDialog';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };

/* -------------------------------------------------------------------------- */
/* helpers                                                                     */
/* -------------------------------------------------------------------------- */
const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const fullImg = (path) => `${process.env.REACT_APP_BACKEND_URL}${path}`;

/** V14.83 — « 45 s », « 2 min », « 1 h 05 ». */
const formatEta = (s) => {
  if (s == null) return '—';
  if (s < 60) return `${Math.max(5, Math.round(s / 5) * 5)} s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}`;
};

// Pretty-print a variation_category + value into a short FR chip label.
const VARIATION_LABELS = {
  time_of_day: { _label: 'Heure', morning: 'Matin', midday: 'Midi', golden_hour: 'Golden hour', blue_hour: 'Blue hour', night: 'Nuit' },
  weather:     { _label: 'Météo', clear: 'Ciel clair', cloudy: 'Nuageux', rain: 'Pluie', snow: 'Neige', fog: 'Brouillard' },
  season:      { _label: 'Saison', spring: 'Printemps', summer: 'Été', autumn: 'Automne', winter: 'Hiver' },
  lighting:    { _label: 'Éclairage', natural: 'Naturel', dim: 'Tamisé', warm: 'Chaud', cool: 'Froid', festive: 'Festif' },
  material:    { _label: 'Matériau', sol: 'Sol', mur: 'Murs', plafond: 'Plafond', mobilier: 'Mobilier', cuisine: 'Cuisine', sdb: 'SDB' },
};

const variationChipText = (item) => {
  if (!item.variation_category) return null;
  const cat = VARIATION_LABELS[item.variation_category] || {};
  const head = cat._label || item.variation_category;
  const val = cat[item.variation_value] || item.variation_value;
  return `${head} · ${val}`;
};

/* -------------------------------------------------------------------------- */
/* Source badge                                                                */
/* -------------------------------------------------------------------------- */
const SourceBadge = ({ source }) => {
  if (source === 'materialtheque') {
    return (
      <span
        className="hud-badge"
        data-testid="gallery-source-materialtheque"
        style={{
          background: 'var(--c-gold-soft)',
          color: 'var(--c-gold)',
          border: '1px solid var(--c-border-accent)',
          fontSize: 9.5,
          padding: '2px 7px',
          letterSpacing: 0.4,
        }}
      >
        <ShieldCheck className="w-2.5 h-2.5" />
        Matériauthèque validée
      </span>
    );
  }
  return (
    <span
      className="hud-badge"
      data-testid="gallery-source-default"
      style={{
        background: 'var(--c-surface-2)',
        color: 'var(--c-text-muted)',
        border: '1px solid var(--c-border)',
        fontSize: 9.5,
        padding: '2px 7px',
        letterSpacing: 0.4,
      }}
    >
      <Cpu className="w-2.5 h-2.5" />
      Configuration par défaut
    </span>
  );
};

/* -------------------------------------------------------------------------- */
/* Tile                                                                        */
/* -------------------------------------------------------------------------- */
const GalleryTile = ({ item, onOpen, onShare, onValidate, onDelete, onVariation, onCompare, onWhiteboxRef, onArchive, onUpscale, index, alwaysShowActions = false }) => {
  // V15.22 — cockpit : actions toujours visibles sur les miniatures (plus de survol requis).
  const hov = alwaysShowActions ? 'opacity-100' : 'opacity-0 group-hover:opacity-100';
  // Pseudo-masonry: deterministic varying aspect via index
  const aspect = useMemo(() => {
    const cycle = ['aspect-[4/5]', 'aspect-[1/1]', 'aspect-[4/3]', 'aspect-[3/4]'];
    return cycle[index % cycle.length];
  }, [index]);

  const isCompleted = item.status === 'completed' && item.image_url;
  const isValidated = !!item.is_validated;
  const isRejected = item.review_status === 'rejected';
  const isArchived = !!item.is_archived;
  const isVariation = !!item.parent_job_id;
  const varText = variationChipText(item);
  const qa = typeof item.utility_score === 'number' ? item.utility_score : null;
  const qaColor = qa === null ? null : qa >= 80
    ? { bg: 'rgba(34,197,94,0.18)', fg: '#86EFAC', bd: 'rgba(34,197,94,0.4)' }
    : qa >= 55
      ? { bg: 'rgba(251,191,36,0.16)', fg: '#FCD34D', bd: 'rgba(251,191,36,0.38)' }
      : { bg: 'rgba(244,63,94,0.18)', fg: '#FDA4AF', bd: 'rgba(244,63,94,0.42)' };

  return (
    <button
      type="button"
      onClick={() => isCompleted ? onOpen(item) : null}
      data-testid={`gallery-tile-${item.job_id}`}
      data-status={item.status || 'completed'}
      data-validated={isValidated ? 'true' : 'false'}
      data-rejected={isRejected ? 'true' : 'false'}
      data-archived={isArchived ? 'true' : 'false'}
      data-variation={isVariation ? 'true' : 'false'}
      className={`group relative ${aspect} w-full overflow-hidden rounded-xl text-left transition-transform duration-300 ${isCompleted ? 'hover:-translate-y-0.5 cursor-pointer' : 'cursor-default'}`}
      style={{
        background: 'var(--c-surface-2)',
        // Validated renders get a discrete emerald accent border; rejected ones a red dashed border.
        border: isRejected ? '1px dashed rgba(239,68,68,0.6)' : `1px solid ${isValidated ? 'rgba(34,197,94,0.55)' : 'var(--c-border)'}`,
        boxShadow: isValidated
          ? '0 0 0 1px rgba(34,197,94,0.18), 0 18px 38px -22px rgba(0,0,0,0.7)'
          : '0 1px 0 rgba(255,255,255,0.03) inset, 0 18px 38px -22px rgba(0,0,0,0.7)',
      }}
    >
      {item.image_url ? (
        <img
          src={fullImg(item.image_url)}
          alt={`Rendu ${item.lot_number || item.lot_id}`}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
          style={{ filter: isRejected ? 'grayscale(1) brightness(0.55)' : 'saturate(1.02)' }}
        />
      ) : (
        // Pending / processing placeholder — same dimensions as a completed tile
        // so the Style chip stays visible while the render is in flight.
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-2"
          style={{
            background:
              'radial-gradient(circle at 30% 30%, rgba(217,160,91,0.10), transparent 60%), var(--c-surface-1)',
          }}
        >
          <Cpu className="w-7 h-7 animate-pulse" style={{ color: 'var(--c-gold)' }} />
          <p className="text-[11px] font-medium" style={{ color: 'var(--c-text-2)' }}>
            {item.status === 'pending' ? 'En file d\'attente…' : 'Génération en cours…'}
          </p>
          {item.queue && (
            <p className="text-[10.5px] hud-tabular px-2 py-0.5 rounded-full" style={{ background: 'rgba(217,160,91,0.14)', color: '#FDE68A', border: '1px solid rgba(217,160,91,0.35)' }} data-testid={`gen-queue-${item.job_id}`}>
              {item.status === 'pending'
                ? `#${item.queue.position} sur ${item.queue.total_pending} · ~${formatEta(item.queue.eta_seconds)}`
                : `en cours · reste ~${formatEta(item.queue.eta_seconds)}`}
            </p>
          )}
        </div>
      )}
      {/* gradient veil */}
      <span
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(180deg, rgba(8,10,14,0) 35%, rgba(8,10,14,0.45) 75%, rgba(8,10,14,0.85) 100%)',
        }}
      />
      {/* top badges */}
      <div className={alwaysShowActions ? 'absolute top-2.5 left-2.5 right-2.5 flex flex-col gap-1.5' : 'absolute top-2.5 left-2.5 right-2.5 flex items-start justify-between gap-2'}>
        <div className="flex items-center gap-1.5 flex-wrap">
          {isRejected && (
            <span
              className="hud-badge"
              data-testid={`gallery-tile-rejected-${item.job_id}`}
              style={{ background: 'rgba(239,68,68,0.24)', color: '#FCA5A5', border: '1px solid rgba(239,68,68,0.55)', fontSize: 9.5, padding: '2px 7px', letterSpacing: 0.4 }}
              title={`Refusé — trop éloigné du plan${item.review_annotation ? ` · ${item.review_annotation}` : ''}`}
            >
              <ShieldX className="w-2.5 h-2.5" /> Refusé
            </span>
          )}
          {isArchived && (
            <span
              className="hud-badge"
              data-testid={`gallery-tile-archived-${item.job_id}`}
              style={{ background: 'rgba(148,163,184,0.2)', color: '#CBD5E1', border: '1px solid rgba(148,163,184,0.45)', fontSize: 9.5, padding: '2px 7px', letterSpacing: 0.4 }}
              title={`Archivé${item.archived_at ? ` le ${formatDate(item.archived_at)}` : ''}`}
            >
              <Archive className="w-2.5 h-2.5" /> Archivé
            </span>
          )}
          {item.review_rating ? (
            <span
              className="hud-badge"
              data-testid={`gallery-tile-rating-${item.job_id}`}
              style={{ background: 'rgba(250,204,21,0.16)', color: '#FACC15', border: '1px solid rgba(250,204,21,0.4)', fontSize: 9.5, padding: '2px 7px', letterSpacing: 0.4 }}
              title={`Note : ${item.review_rating}/5`}
            >
              <Star className="w-2.5 h-2.5" style={{ fill: '#FACC15' }} /> {item.review_rating}
            </span>
          ) : null}
          {isValidated && (
            <span
              className="hud-badge"
              data-testid={`gallery-tile-validated-${item.job_id}`}
              style={{
                background: 'rgba(34,197,94,0.20)',
                color: '#86EFAC',
                border: '1px solid rgba(34,197,94,0.45)',
                fontSize: 9.5,
                padding: '2px 7px',
                letterSpacing: 0.4,
              }}
              title={`Validé par le client${item.validated_at ? ` le ${formatDate(item.validated_at)}` : ''}`}
            >
              <CheckCircle2 className="w-2.5 h-2.5" />
              Validé
            </span>
          )}
          {item.upscale?.status === 'completed' && (
            <span
              className="hud-badge"
              data-testid={`gallery-tile-4k-${item.job_id}`}
              style={{ background: 'rgba(217,160,91,0.22)', color: 'var(--c-gold)', border: '1px solid var(--c-border-accent)', fontSize: 9.5, padding: '2px 7px', letterSpacing: 0.4 }}
              title={`Livrable 4K prêt · ${item.upscale.width}×${item.upscale.height}${item.upscale.cutout_path ? ' · détouré' : ''}`}
            >
              <Scaling className="w-2.5 h-2.5" />
              4K{item.upscale.cutout_path ? ' · détouré' : ''}
            </span>
          )}
          {item.upscale?.status === 'processing' && (
            <span className="hud-badge" data-testid={`gallery-tile-4k-processing-${item.job_id}`}
                  style={{ background: 'rgba(8,10,14,0.7)', color: 'var(--c-text-2)', border: '1px solid var(--c-border)', fontSize: 9.5, padding: '2px 7px' }}>
              <Hourglass className="w-2.5 h-2.5" /> 4K en cours
            </span>
          )}
          {isVariation && varText && (
            <span
              className="hud-badge"
              data-testid={`gallery-tile-variation-${item.job_id}`}
              style={{
                background: 'rgba(147,197,253,0.18)',
                color: '#93C5FD',
                border: '1px solid rgba(147,197,253,0.35)',
                fontSize: 9.5,
                padding: '2px 7px',
                letterSpacing: 0.4,
              }}
              title={item.variation_note ? `Note: ${item.variation_note}` : varText}
            >
              <GitBranch className="w-2.5 h-2.5" />
              {varText}
            </span>
          )}
          {!isVariation && <SourceBadge source={item.config_source} />}
          {item.params?.render_stage === 'whitebox' && (
            <span
              className="hud-badge"
              data-testid={`gallery-tile-whitebox-${item.job_id}`}
              style={{ background: 'rgba(255,255,255,0.85)', color: '#0F1115', border: '1px solid rgba(255,255,255,0.9)', fontSize: 9.5, padding: '2px 7px', letterSpacing: 0.4 }}
              title="Étape 1/4 — maquette blanche (géométrie + caméra)"
            >
              Maquette blanche
            </span>
          )}
          {item.params?.render_stage === 'textured' && (
            <span
              className="hud-badge"
              data-testid={`gallery-tile-textured-${item.job_id}`}
              style={{ background: 'rgba(217,160,91,0.22)', color: '#F5C77E', border: '1px solid rgba(217,160,91,0.5)', fontSize: 9.5, padding: '2px 7px', letterSpacing: 0.4 }}
              title="Étape 2/4 — texturé sur la maquette blanche de référence"
            >
              Texturé 2/4
            </span>
          )}
          {item.params?.render_stage === 'furnished' && (
            <span
              className="hud-badge"
              data-testid={`gallery-tile-furnished-${item.job_id}`}
              style={{ background: 'rgba(94,234,212,0.18)', color: '#5EEAD4', border: '1px solid rgba(94,234,212,0.45)', fontSize: 9.5, padding: '2px 7px', letterSpacing: 0.4 }}
              title="Étape 3/4 — meublé sur le rendu texturé de référence"
            >
              Meublé 3/4
            </span>
          )}
          {item.params?.render_stage === 'final' && (
            <span
              className="hud-badge"
              data-testid={`gallery-tile-final-${item.job_id}`}
              style={{ background: 'rgba(250,204,21,0.18)', color: '#FACC15', border: '1px solid rgba(250,204,21,0.45)', fontSize: 9.5, padding: '2px 7px', letterSpacing: 0.4 }}
              title="Étape 4/4 — lumière & post-production sur le rendu meublé de référence"
            >
              Final 4/4
            </span>
          )}
          {item.params?.engine === 'gpt_image_1' && (
            <span className="hud-badge" data-testid={`gallery-tile-engine-${item.job_id}`}
                  style={{ background: 'rgba(16,185,129,0.16)', color: '#6EE7B7', border: '1px solid rgba(16,185,129,0.45)', fontSize: 9.5, padding: '2px 7px', letterSpacing: 0.4 }}
                  title="Moteur : OpenAI GPT Image 1 (édition image-to-image)">
              GPT Image 1
            </span>
          )}
          {item.params?.duel_id && (
            <span className="hud-badge" data-testid={`gallery-tile-duel-${item.job_id}`}
                  style={{ background: 'rgba(217,160,91,0.18)', color: '#F5C77E', border: '1px solid rgba(217,160,91,0.5)', fontSize: 9.5, padding: '2px 7px', letterSpacing: 0.4 }}
                  title={`Duel ${item.params.duel_id} — même référence, deux moteurs : ancrez le gagnant`}>
              Duel
            </span>
          )}
          {item.is_furnished_reference && (
            <span
              className="hud-badge"
              data-testid={`gallery-tile-furnished-ref-${item.job_id}`}
              style={{ background: 'rgba(244,114,182,0.2)', color: '#F9A8D4', border: '1px solid rgba(244,114,182,0.45)', fontSize: 9.5, padding: '2px 7px', letterSpacing: 0.4 }}
              title="Rendu meublé de référence du lot — l'étape 4 s'y accroche"
            >
              <Anchor className="w-2.5 h-2.5" /> Référence meublé
            </span>
          )}
          {item.is_textured_reference && (
            <span
              className="hud-badge"
              data-testid={`gallery-tile-textured-ref-${item.job_id}`}
              style={{ background: 'rgba(244,114,182,0.2)', color: '#F9A8D4', border: '1px solid rgba(244,114,182,0.45)', fontSize: 9.5, padding: '2px 7px', letterSpacing: 0.4 }}
              title="Rendu texturé de référence du lot — l'étape 3 s'y accroche"
            >
              <Anchor className="w-2.5 h-2.5" /> Référence texturé
            </span>
          )}
          {typeof item.fidelity_score === 'number' && (() => {
            const f = item.fidelity_score;
            const c = f >= 80 ? { bg: 'rgba(34,197,94,0.18)', fg: '#4ADE80', bd: 'rgba(34,197,94,0.45)' }
              : f >= 60 ? { bg: 'rgba(250,204,21,0.16)', fg: '#FACC15', bd: 'rgba(250,204,21,0.45)' }
              : { bg: 'rgba(239,68,68,0.18)', fg: '#F87171', bd: 'rgba(239,68,68,0.45)' };
            return (
              <span
                className="hud-badge"
                data-testid={`gallery-tile-fidelity-${item.job_id}`}
                style={{ background: c.bg, color: c.fg, border: `1px solid ${c.bd}`, fontSize: 9.5, padding: '2px 7px', letterSpacing: 0.4 }}
                title={`Fidélité au plan (silhouette masque ↔ rendu) : ${item.fidelity_report?.summary || ''}`}
              >
                Plan {f}%
              </span>
            );
          })()}
          {item.is_whitebox_reference && (
            <span
              className="hud-badge"
              data-testid={`gallery-tile-whitebox-ref-${item.job_id}`}
              style={{ background: 'rgba(244,114,182,0.2)', color: '#F9A8D4', border: '1px solid rgba(244,114,182,0.45)', fontSize: 9.5, padding: '2px 7px', letterSpacing: 0.4 }}
              title="Maquette de référence du lot — les étapes 2-4 s'y accrochent"
            >
              <Anchor className="w-2.5 h-2.5" /> Référence lot
            </span>
          )}
          {qa !== null && (
            <span
              className="hud-badge"
              data-testid={`gallery-tile-qa-${item.job_id}`}
              style={{
                background: qaColor.bg,
                color: qaColor.fg,
                border: `1px solid ${qaColor.bd}`,
                fontSize: 9.5,
                padding: '2px 7px',
                letterSpacing: 0.4,
              }}
              title={item.critic_report?.summary || 'Score de conformité au plan (Critic IA)'}
            >
              QA {qa}
            </span>
          )}
          {item.view_type === 'fps' && (
            <span
              className="hud-badge"
              data-testid={`gallery-tile-fps-${item.job_id}`}
              style={{
                background: 'rgba(94,234,212,0.18)',
                color: '#5EEAD4',
                border: '1px solid rgba(94,234,212,0.32)',
                fontSize: 9.5,
                padding: '2px 7px',
                letterSpacing: 0.4,
              }}
              title={`Vue FPS · ${item.fov_deg || 75}° · angle ${item.camera_position?.angle ?? 0}°`}
            >
              FPS · {item.fov_deg || 75}°
            </span>
          )}
          {item.style_name && (
            <span
              className="hud-badge"
              data-testid={`gallery-tile-style-${item.job_id}`}
              data-style-id={item.style_decoration_id || ''}
              title={`Style verrouillé : ${item.style_name}${item.style_materials_count ? ` · ${item.style_materials_count} matériaux Matériauthèque` : ''}`}
              style={{
                background: 'rgba(217,160,91,0.18)',
                color: 'var(--c-gold)',
                border: '1px solid var(--c-border-accent)',
                fontSize: 9.5,
                padding: '2px 7px',
                letterSpacing: 0.4,
                maxWidth: 130,
              }}
            >
              <Sparkles className="w-2.5 h-2.5 shrink-0" />
              <span className="truncate">{item.style_name}</span>
            </span>
          )}
        </div>
        {/* Top-right action cluster — visible on hover (always interactive) ; toujours visible + retour à la ligne en mode cockpit */}
        <span className={alwaysShowActions ? 'flex items-center gap-1.5 flex-wrap justify-end' : 'flex items-center gap-1.5'}>
          {isCompleted && !isRejected && ['whitebox', 'textured', 'furnished'].includes(item.params?.render_stage) && (() => {
            const isRef = item.is_whitebox_reference || item.is_textured_reference || item.is_furnished_reference;
            const what = item.params?.render_stage === 'whitebox' ? 'maquette blanche' : item.params?.render_stage === 'textured' ? 'rendu texturé' : 'rendu meublé';
            return (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); onWhiteboxRef?.(item); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onWhiteboxRef?.(item); } }}
                data-testid={`gallery-tile-whitebox-ref-btn-${item.job_id}`}
                className="transition-all rounded-md p-1.5 cursor-pointer hover:scale-110"
                style={{
                  background: isRef ? 'rgba(244,114,182,0.22)' : 'rgba(8,10,14,0.7)',
                  border: `1px solid ${isRef ? 'rgba(244,114,182,0.5)' : 'var(--c-border)'}`,
                }}
                title={isRef ? `Retirer la référence (${what})` : `Définir ce ${what} comme référence du lot`}
              >
                <Anchor className="w-3.5 h-3.5" style={{ color: isRef ? '#F9A8D4' : 'var(--c-text-muted)' }} />
              </span>
            );
          })()}
          {isCompleted && !isVariation && !isRejected && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); onValidate?.(item); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onValidate?.(item); } }}
              data-testid={`gallery-tile-validate-${item.job_id}`}
              className="transition-all rounded-md p-1.5 cursor-pointer hover:scale-110"
              style={{
                background: isValidated ? 'rgba(34,197,94,0.20)' : 'rgba(8,10,14,0.7)',
                border: `1px solid ${isValidated ? 'rgba(34,197,94,0.45)' : 'var(--c-border)'}`,
                opacity: isValidated ? 1 : 0.85,
              }}
              title={isValidated ? 'Retirer la validation' : 'Valider ce rendu'}
              aria-label={isValidated ? 'Retirer la validation' : 'Valider ce rendu'}
            >
              <CheckCircle2 className="w-3.5 h-3.5" style={{ color: isValidated ? '#86EFAC' : 'var(--c-text-2)' }} />
            </span>
          )}
          {isCompleted && isValidated && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); onUpscale?.(item); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onUpscale?.(item); } }}
              data-testid={`gallery-tile-upscale-${item.job_id}`}
              className="transition-all rounded-md p-1.5 cursor-pointer hover:scale-110"
              style={{
                background: item.upscale?.status === 'completed' ? 'rgba(217,160,91,0.22)' : 'rgba(8,10,14,0.7)',
                border: `1px solid ${item.upscale?.status === 'completed' ? 'var(--c-border-accent)' : 'var(--c-border)'}`,
              }}
              title={item.upscale?.status === 'completed' ? `Livrable 4K prêt (${item.upscale.width}×${item.upscale.height}) — télécharger / relancer` : 'Agrandir en 4K (livrable client)'}
              aria-label="Agrandir en 4K"
            >
              <Scaling className="w-3.5 h-3.5" style={{ color: item.upscale?.status === 'completed' ? 'var(--c-gold)' : 'var(--c-text-2)' }} />
            </span>
          )}
          {isCompleted && isValidated && !isVariation && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); onVariation?.(item); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onVariation?.(item); } }}
              data-testid={`gallery-tile-variation-btn-${item.job_id}`}
              className={`${hov} transition-opacity rounded-md p-1.5 cursor-pointer`}
              style={{ background: 'rgba(217,160,91,0.20)', border: '1px solid var(--c-border-accent)' }}
              title="Générer une variation (heure / météo / matériau…)"
              aria-label="Générer une variation"
            >
              <Wand2 className="w-3.5 h-3.5" style={{ color: 'var(--c-gold)' }} />
            </span>
          )}
          {isCompleted && isVariation && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); onCompare?.(item); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onCompare?.(item); } }}
              data-testid={`gallery-tile-compare-${item.job_id}`}
              className="transition-all rounded-md p-1.5 cursor-pointer hover:scale-110"
              style={{ background: 'rgba(147,197,253,0.20)', border: '1px solid rgba(147,197,253,0.45)' }}
              title="Comparer avant / après avec le rendu validé"
              aria-label="Comparer avant après"
            >
              <GitCompare className="w-3.5 h-3.5" style={{ color: '#93C5FD' }} />
            </span>
          )}
          {isCompleted && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); onShare?.(item); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onShare?.(item); } }}
              data-testid={`gallery-tile-share-${item.job_id}`}
              className={`${hov} transition-opacity rounded-md p-1.5 cursor-pointer`}
              style={{ background: 'rgba(8,10,14,0.7)', border: '1px solid var(--c-border)' }}
              title="Partager au client"
              aria-label="Partager au client"
            >
              <Share2 className="w-3.5 h-3.5" style={{ color: 'var(--c-gold)' }} />
            </span>
          )}
          {item.status !== 'pending' && item.status !== 'processing' && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); onArchive?.(item); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onArchive?.(item); } }}
              data-testid={`gallery-tile-archive-${item.job_id}`}
              className={`${isArchived ? '' : hov} transition-opacity rounded-md p-1.5 cursor-pointer`}
              style={{ background: isArchived ? 'rgba(148,163,184,0.22)' : 'rgba(8,10,14,0.7)', border: `1px solid ${isArchived ? 'rgba(148,163,184,0.5)' : 'var(--c-border)'}` }}
              title={isArchived ? 'Désarchiver — remettre dans la galerie' : 'Archiver — masquer de la galerie (réversible)'}
              aria-label={isArchived ? 'Désarchiver ce rendu' : 'Archiver ce rendu'}
            >
              {isArchived
                ? <ArchiveRestore className="w-3.5 h-3.5" style={{ color: '#CBD5E1' }} />
                : <Archive className="w-3.5 h-3.5" style={{ color: 'var(--c-text-2)' }} />}
            </span>
          )}
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); onDelete?.(item); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onDelete?.(item); } }}
            data-testid={`gallery-tile-delete-${item.job_id}`}
            className={`${hov} transition-opacity rounded-md p-1.5 cursor-pointer`}
            style={{ background: 'rgba(8,10,14,0.7)', border: '1px solid rgba(239,68,68,0.4)' }}
            title="Supprimer ce rendu"
            aria-label="Supprimer ce rendu"
          >
            <Trash2 className="w-3.5 h-3.5" style={{ color: '#FCA5A5' }} />
          </span>
          {isCompleted && (
            <span
              aria-hidden
              className={`${hov} transition-opacity rounded-md p-1.5`}
              style={{ background: 'rgba(8,10,14,0.7)', border: '1px solid var(--c-border)' }}
            >
              <Maximize2 className="w-3.5 h-3.5" style={{ color: 'var(--c-gold)' }} />
            </span>
          )}
        </span>
      </div>
      {/* bottom meta */}
      <div className="absolute bottom-0 left-0 right-0 p-3">
        <div className="flex items-center gap-2 mb-1">
          {item.lot_number && (
            <span
              className="text-[10px] uppercase tracking-[0.14em] font-semibold"
              style={{ color: 'var(--c-gold)' }}
            >
              Lot {item.lot_number}
            </span>
          )}
          {item.typology && (
            <span
              className="text-[10px] uppercase tracking-[0.14em]"
              style={{ color: 'var(--c-text-muted)' }}
            >
              · {item.typology}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-[11px]" style={{ color: 'var(--c-text-2)' }}>
          <Calendar className="w-3 h-3" strokeWidth={1.75} />
          {formatDate(item.completed_at)}
        </div>
      </div>
    </button>
  );
};

/* -------------------------------------------------------------------------- */
/* Lightbox                                                                    */
/* -------------------------------------------------------------------------- */
const Lightbox = ({ item, onClose, projectId, onShare, onValidate, onVariation, onCompare, onDelete, onWhiteboxRef }) => {
  // close on ESC
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  if (!item) return null;

  const isValidated = !!item.is_validated;
  const isVariation = !!item.parent_job_id;
  const varText = variationChipText(item);

  const params = item.params || {};
  const paramRows = [
    ['Étape',           params.render_stage === 'whitebox' ? 'Maquette blanche (1/4)' : params.render_stage === 'textured' ? 'Texturé (2/4)' : params.render_stage === 'furnished' ? 'Meublé (3/4)' : params.render_stage === 'final' ? 'Final (4/4)' : null],
    ['Type de bien',    params.type_bien],
    ['Style design',    params.style_design || params.style],
    ['Étage / niveau',  params.etage_niveau],
    ['Moment journée',  params.moment_journee],
    ['Vue / angle',     params.vue_angle],
    ['Mobilier',        params.catalogue_mobilier],
    ['Échelle plan',    params.echelle_plan],
  ].filter(([, v]) => v);

  return (
    <div
      className="absolute inset-0 z-[60] flex items-start justify-center p-3 sm:p-6 overflow-y-auto"
      data-testid="gallery-lightbox"
      style={{ background: 'rgba(6,8,12,0.92)', backdropFilter: 'blur(10px)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative w-full max-w-5xl rounded-2xl overflow-hidden flex flex-col lg:flex-row"
        style={{
          background: 'var(--c-surface-1)',
          border: '1px solid var(--c-border)',
          boxShadow: '0 30px 80px -20px rgba(0,0,0,0.85)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Image side */}
        <div
          className="relative flex-1 min-h-[260px] lg:min-h-[480px] flex items-center justify-center"
          style={{ background: '#06080c' }}
        >
          <img
            src={fullImg(item.image_url)}
            alt={`Rendu ${item.lot_number || item.lot_id}`}
            className="max-w-full max-h-[55vh] lg:max-h-[70vh] object-contain"
          />
          <button
            type="button"
            onClick={onClose}
            data-testid="gallery-lightbox-close"
            className="absolute top-3 right-3 rounded-lg p-2 transition-colors"
            style={{
              background: 'rgba(8,10,14,0.7)',
              border: '1px solid var(--c-border)',
              color: 'var(--c-text-2)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--c-gold)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--c-text-2)'; }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Info side */}
        <aside
          className="w-full lg:w-[360px] shrink-0 p-5 sm:p-6 overflow-y-auto"
          style={{ background: 'var(--c-surface-1)', borderLeft: '1px solid var(--c-border)' }}
        >
          <span className="hud-eyebrow hud-eyebrow-gold">Rendu IA</span>
          <h3
            className="mt-1 font-semibold tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 20 }}
          >
            Lot {item.lot_number || item.lot_id?.slice(0, 8)}
            {item.typology ? ` · ${item.typology}` : ''}
          </h3>

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {isValidated && (
              <span
                className="hud-badge"
                style={{
                  background: 'rgba(34,197,94,0.20)', color: '#86EFAC',
                  border: '1px solid rgba(34,197,94,0.45)', fontSize: 10, padding: '2px 8px',
                }}
                data-testid="gallery-lightbox-validated-chip"
              >
                <CheckCircle2 className="w-2.5 h-2.5" />
                Validé client
              </span>
            )}
            {isVariation && varText && (
              <span
                className="hud-badge"
                style={{
                  background: 'rgba(147,197,253,0.20)', color: '#93C5FD',
                  border: '1px solid rgba(147,197,253,0.45)', fontSize: 10, padding: '2px 8px',
                }}
              >
                <GitBranch className="w-2.5 h-2.5" />
                {varText}
              </span>
            )}
            <SourceBadge source={item.config_source} />
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3">
            <div
              className="rounded-lg p-3"
              style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}
            >
              <div className="flex items-center gap-1.5 mb-1" style={{ color: 'var(--c-text-muted)' }}>
                <Calendar className="w-3 h-3" strokeWidth={1.75} />
                <span className="text-[10px] uppercase tracking-[0.14em]">Généré le</span>
              </div>
              <p className="text-[12.5px]" style={{ color: 'var(--c-text)' }}>
                {formatDate(item.completed_at)}
              </p>
            </div>
            <div
              className="rounded-lg p-3"
              style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}
            >
              <div className="flex items-center gap-1.5 mb-1" style={{ color: 'var(--c-text-muted)' }}>
                <Clock className="w-3 h-3" strokeWidth={1.75} />
                <span className="text-[10px] uppercase tracking-[0.14em]">Durée</span>
              </div>
              <p className="text-[12.5px]" style={{ color: 'var(--c-text)' }}>
                {item.generation_time_seconds
                  ? `${item.generation_time_seconds.toFixed(1)} s`
                  : '—'}
              </p>
            </div>
          </div>

          {paramRows.length > 0 && (
            <div className="mt-5">
              <span className="hud-eyebrow">Paramètres prompt</span>
              <ul className="mt-2 space-y-1.5">
                {paramRows.map(([label, value]) => (
                  <li
                    key={label}
                    className="flex items-baseline justify-between gap-3 text-[12px] py-1"
                    style={{ borderBottom: '1px dashed var(--c-border)' }}
                  >
                    <span style={{ color: 'var(--c-text-muted)' }}>{label}</span>
                    <span style={{ color: 'var(--c-text)' }} className="text-right truncate">
                      {String(value)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-6 flex flex-col gap-2">
            {item.params?.render_stage === 'whitebox' && item.status === 'completed' && (
              <HudButton
                variant={item.is_whitebox_reference ? 'secondary' : 'primary'}
                size="sm"
                className="w-full justify-center"
                onClick={() => onWhiteboxRef?.(item)}
                leftIcon={<Anchor className="w-3.5 h-3.5" />}
                data-testid="gallery-lightbox-whitebox-ref"
              >
                {item.is_whitebox_reference ? 'Retirer la référence du lot' : 'Définir comme maquette de référence du lot'}
              </HudButton>
            )}
            {/* Primary action — Validate (only on non-variation completed) */}
            {!isVariation && (
              <HudButton
                variant={isValidated ? 'secondary' : 'primary'}
                size="sm"
                className="w-full justify-center"
                onClick={() => onValidate?.(item)}
                leftIcon={<CheckCircle2 className="w-3.5 h-3.5" />}
                data-testid="gallery-lightbox-validate"
              >
                {isValidated ? 'Retirer la validation' : 'Valider ce rendu'}
              </HudButton>
            )}
            {/* Variation — only when validated and not already a variation */}
            {isValidated && !isVariation && (
              <HudButton
                variant="primary"
                size="sm"
                className="w-full justify-center"
                onClick={() => onVariation?.(item)}
                leftIcon={<Wand2 className="w-3.5 h-3.5" />}
                data-testid="gallery-lightbox-variation"
              >
                Générer une variation
              </HudButton>
            )}
            {/* Compare — only for variation tiles */}
            {isVariation && (
              <HudButton
                variant="primary"
                size="sm"
                className="w-full justify-center"
                onClick={() => onCompare?.(item)}
                leftIcon={<GitCompare className="w-3.5 h-3.5" />}
                data-testid="gallery-lightbox-compare"
              >
                Comparer avant / après
              </HudButton>
            )}
            <HudButton
              variant="secondary"
              size="sm"
              className="w-full justify-center"
              onClick={() => onShare?.(item)}
              leftIcon={<Share2 className="w-3.5 h-3.5" />}
              data-testid="gallery-lightbox-share"
            >
              Partager au client
            </HudButton>
            <a
              href={fullImg(item.image_url)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="gallery-lightbox-open-fullsize"
            >
              <HudButton
                variant="ghost"
                size="sm"
                className="w-full justify-center"
                rightIcon={<ExternalLink className="w-3.5 h-3.5" />}
              >
                Ouvrir en pleine taille
              </HudButton>
            </a>
            <Link to={`/generation?project=${projectId}&lot=${item.lot_id}`}>
              <HudButton
                variant="ghost"
                size="sm"
                className="w-full justify-center"
                rightIcon={<RefreshCw className="w-3.5 h-3.5" />}
                data-testid="gallery-lightbox-regenerate"
              >
                Re-générer ce lot
              </HudButton>
            </Link>
            <button
              type="button"
              onClick={() => { onClose(); onDelete?.(item); }}
              data-testid="gallery-lightbox-delete"
              className="w-full mt-2 rounded-lg py-2 text-[12.5px] font-medium transition-colors"
              style={{
                background: 'transparent',
                color: '#FCA5A5',
                border: '1px solid rgba(239,68,68,0.35)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(239,68,68,0.10)';
                e.currentTarget.style.color = '#FECACA';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#FCA5A5';
              }}
            >
              <Trash2 className="w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5" />
              Supprimer ce rendu
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Empty / Loading                                                             */
/* -------------------------------------------------------------------------- */
const EmptyState = ({ projectId }) => (
  <div
    className="rounded-xl border border-dashed flex flex-col items-center justify-center py-14 px-6 text-center"
    style={{ borderColor: 'rgba(255,255,255,0.10)' }}
    data-testid="gallery-empty"
  >
    <div
      className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
      style={{
        background: 'var(--c-surface-2)',
        border: '1px solid var(--c-border)',
        color: 'var(--c-gold)',
      }}
    >
      <ImageIcon className="w-5 h-5" strokeWidth={1.6} />
    </div>
    <p className="font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 15 }}>
      Aucun rendu pour l'instant
    </p>
    <p className="text-[12.5px] mt-1 max-w-sm" style={{ color: 'var(--c-text-2)' }}>
      Lancez votre première génération depuis le studio. Les rendus apparaîtront ici dès qu'ils seront prêts.
    </p>
    <div className="mt-4">
      <Link to={`/generation?project=${projectId}`}>
        <HudButton variant="primary" size="sm" data-testid="gallery-empty-cta">
          Studio de génération
        </HudButton>
      </Link>
    </div>
  </div>
);

const SkeletonGrid = () => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="gallery-skeleton">
    {Array.from({ length: 8 }).map((_, i) => (
      <div
        key={i}
        className="aspect-[4/5] rounded-xl"
        style={{
          background: 'linear-gradient(90deg, var(--c-surface-2) 0%, var(--c-surface-3) 50%, var(--c-surface-2) 100%)',
          backgroundSize: '200% 100%',
          animation: 'hud-shimmer 1.4s ease-in-out infinite',
          border: '1px solid var(--c-border)',
        }}
      />
    ))}
  </div>
);

/* -------------------------------------------------------------------------- */
/* Sidebar tree (Bâtiment → Étage → Lot)                                       */
/* -------------------------------------------------------------------------- */
const TreeRow = ({ icon: Icon, label, count, active, onClick, depth = 0, expandable, expanded, onToggleExpand, testid }) => (
  <div
    onClick={onClick}
    data-testid={testid}
    className="flex items-center gap-1.5 px-2 py-1.5 rounded-md cursor-pointer text-[12px] transition-colors"
    style={{
      paddingLeft: 8 + depth * 14,
      background: active ? 'rgba(217,160,91,0.16)' : 'transparent',
      color: active ? 'var(--c-gold)' : 'var(--c-text-2)',
      borderLeft: `2px solid ${active ? 'var(--c-gold)' : 'transparent'}`,
    }}
    onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'var(--c-surface-2)'; }}
    onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
  >
    {expandable ? (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggleExpand?.(); }}
        className="shrink-0 hover:text-amber-400 transition-colors"
        style={{ color: 'var(--c-text-muted)' }}
        aria-label={expanded ? 'Réduire' : 'Développer'}
      >
        {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
      </button>
    ) : (
      <span className="w-3.5 shrink-0" />
    )}
    {Icon && <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.6} />}
    <span className="truncate flex-1">{label}</span>
    <span className="text-[10px] tabular-nums" style={{ color: 'var(--c-text-muted)' }}>{count}</span>
  </div>
);

const GalleryTree = ({
  tree, totalCount, selected, onSelect,
  expandedBuildings, setExpandedBuildings,
  expandedFloors, setExpandedFloors,
}) => {
  const toggleBuilding = (id) => setExpandedBuildings((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleFloor = (key) => setExpandedFloors((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  return (
    <aside
      className="w-[220px] shrink-0 hidden md:block"
      data-testid="gallery-tree"
      style={{
        background: 'var(--c-surface-2)',
        border: '1px solid var(--c-border)',
        borderRadius: 12,
        padding: 8,
        maxHeight: 600,
        overflowY: 'auto',
      }}
    >
      <div className="px-2 pt-1 pb-2">
        <span className="hud-eyebrow">Arborescence</span>
      </div>
      <TreeRow
        label="Tous les rendus"
        count={totalCount}
        icon={Layers}
        active={selected.type === 'all'}
        onClick={() => onSelect({ type: 'all' })}
        testid="tree-node-all"
      />
      {tree.map((b) => {
        const bExpanded = expandedBuildings.has(b.id);
        return (
          <div key={b.id}>
            <TreeRow
              label={b.name}
              count={b.count}
              icon={Building2}
              active={selected.type === 'building' && selected.id === b.id}
              onClick={() => onSelect({ type: 'building', id: b.id })}
              expandable
              expanded={bExpanded}
              onToggleExpand={() => toggleBuilding(b.id)}
              testid={`tree-node-building-${b.id}`}
            />
            {bExpanded && b.floors.map((f) => {
              const fKey = `${b.id}::${f.id}`;
              const fExpanded = expandedFloors.has(fKey);
              return (
                <div key={f.id}>
                  <TreeRow
                    label={f.name}
                    count={f.count}
                    icon={Layers3}
                    active={selected.type === 'floor' && selected.id === f.id}
                    onClick={() => onSelect({ type: 'floor', id: f.id })}
                    depth={1}
                    expandable
                    expanded={fExpanded}
                    onToggleExpand={() => toggleFloor(fKey)}
                    testid={`tree-node-floor-${f.id}`}
                  />
                  {fExpanded && f.lots.map((l) => (
                    <TreeRow
                      key={l.id}
                      label={l.typology ? `${l.name} · ${l.typology}` : l.name}
                      count={l.count}
                      icon={Home}
                      active={selected.type === 'lot' && selected.id === l.id}
                      onClick={() => onSelect({ type: 'lot', id: l.id })}
                      depth={2}
                      testid={`tree-node-lot-${l.id}`}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        );
      })}
    </aside>
  );
};

/* -------------------------------------------------------------------------- */
/* Main                                                                        */
/* -------------------------------------------------------------------------- */
const ProjectGenerationGallery = ({ projectId, refreshNonce = 0, lotId = null, stageFilter = 'all', compact = false, onItemsLoaded }) => {
  // V15.21 — mode « lot unique » (cockpit Production) : items du lot, grille compacte, sans arborescence.
  const singleLot = !!lotId;
  const [items, setItems] = useState([]);
  const [lots, setLots] = useState([]); // V14.5 D2 — lots map for cover state
  const [annotateItem, setAnnotateItem] = useState(null); // V14.5 D3 — open annotation dialog for this item
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all | materialtheque | default
  const [statusFilter, setStatusFilter] = useState('all'); // all | pending | review | validated | variations | archived
  const [styleFilter, setStyleFilter] = useState('all'); // 'all' | style_id (chip filter)
  const [treeNode, setTreeNode] = useState({ type: 'all' }); // { type: 'all' | 'building' | 'floor' | 'lot', id?: str }
  const [expandedBuildings, setExpandedBuildings] = useState(() => new Set());
  const [expandedFloors, setExpandedFloors] = useState(() => new Set());
  const [active, setActive] = useState(null);
  const [shareItem, setShareItem] = useState(null);
  const [variationItem, setVariationItem] = useState(null);
  const [upscaleItem, setUpscaleItem] = useState(null); // V15.29 — dialog « Agrandir en 4K »
  const [deleteItem, setDeleteItem] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [compareItem, setCompareItem] = useState(null); // {parent, variation}

  const load = useCallback(async (silent = false) => {
    if (!projectId) return;
    if (silent !== true) setLoading(true);
    setError(null);
    try {
      const [genRes, lotsRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/generations`, opts),
        axios.get(`${API}/projects/${projectId}/lots`, opts).catch(() => ({ data: { items: [] } })),
      ]);
      const all = Array.isArray(genRes.data?.items) ? genRes.data.items : [];
      setItems(all);
      onItemsLoaded?.(all);
      const lotsArr = Array.isArray(lotsRes.data) ? lotsRes.data : (lotsRes.data?.items || []);
      setLots(lotsArr);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Impossible de charger la galerie.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load, refreshNonce]);

  // V15.15 — actualisation : ancrage / validation / maquette blanche depuis n'importe où, retour sur l'onglet.
  useRefsChanged(useCallback(() => load(true), [load]));
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') load(true); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [load]);

  // V14.83 — rafraîchissement auto tant qu'il reste des jobs en attente / en cours,
  // V15.15 — ou qu'un rendu fraîchement terminé attend encore son score de fidélité (juge asynchrone).
  const hasActive = useMemo(() => items.some((i) => {
    if (i.status === 'pending' || i.status === 'processing') return true;
    if (i.upscale?.status === 'processing') return true;
    if (i.status !== 'completed' || i.fidelity_report || i.deterministic) return false;
    const done = Date.parse(i.completed_at || i.created_at || 0);
    return Number.isFinite(done) && (Date.now() - done) < 4 * 60 * 1000;
  }), [items]);
  useEffect(() => {
    if (!hasActive) return undefined;
    const t = setInterval(() => load(true), 8000);
    return () => clearInterval(t);
  }, [hasActive, load]);

  const queueSummary = useMemo(() => {
    const active = items.filter((i) => i.queue && (i.status === 'pending' || i.status === 'processing'));
    if (!active.length) return null;
    const pending = active.filter((i) => i.status === 'pending');
    const processing = active.filter((i) => i.status === 'processing');
    const etas = active.map((i) => i.queue.eta_seconds).filter((e) => e != null);
    return {
      pending: pending.length,
      processing: processing.length,
      next: etas.length ? Math.min(...etas) : null,
      last: etas.length ? Math.max(...etas) : null,
      concurrency: active[0].queue.concurrency,
      totalGlobal: active[0].queue.total_pending,
    };
  }, [items]);

  // --- Action handlers ---
  const handleValidate = useCallback(async (item) => {
    try {
      const r = await axios.post(
        `${API}/projects/${projectId}/generations/${item.job_id}/validate`,
        {}, opts,
      );
      const newState = r.data?.is_validated;
      toast.success(newState ? 'Rendu validé. Variations disponibles.' : 'Validation retirée.');
      // Optimistic local update so the UI flips instantly without waiting for reload.
      setItems((prev) => prev.map((it) => it.job_id === item.job_id
        ? { ...it, is_validated: newState, validated_at: r.data?.validated_at || null }
        : it));
      // V14.93 — prévient le lanceur (étape suivante + config rechargée).
      const stage = r.data?.stage || item.params?.render_stage;
      if (stage) emitRefsChanged({ lotId: item.lot_id, stage, anchored: !!(newState && (r.data?.auto_anchored || item.is_whitebox_reference || item.is_textured_reference || item.is_furnished_reference || stage === 'final')) });
      if (r.data?.auto_anchored) load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la validation.');
    }
  }, [projectId, load]);

  // V15.29 — état du livrable 4K (mis à jour par le dialog ; la galerie poll aussi tant que « processing »)
  const handleUpscaleChanged = useCallback((jobId, upscale) => {
    setItems((prev) => prev.map((it) => it.job_id === jobId ? { ...it, upscale } : it));
    setActive((cur) => cur && cur.job_id === jobId ? { ...cur, upscale } : cur);
    setUpscaleItem((cur) => cur && cur.job_id === jobId ? { ...cur, upscale } : cur);
  }, []);

  // V15.10 — dossier Archives (toggle, optimiste)
  const handleArchive = useCallback(async (item) => {
    try {
      const r = await axios.post(`${API}/projects/${projectId}/generations/${item.job_id}/archive`, {}, opts);
      const newState = !!r.data?.is_archived;
      setItems((prev) => prev.map((it) => it.job_id === item.job_id
        ? { ...it, is_archived: newState, archived_at: r.data?.archived_at || null }
        : it));
      toast.success(newState ? 'Rendu archivé — retrouvez-le dans le dossier Archives.' : 'Rendu désarchivé.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'archivage.");
    }
  }, [projectId]);

  const handleWhiteboxRef = useCallback(async (item) => {
    try {
      const r = await axios.post(`${API}/projects/${projectId}/generations/${item.job_id}/stage-reference`, {}, opts);
      const on = !!r.data?.is_stage_reference;
      const flag = r.data?.stage === 'textured' ? 'is_textured_reference' : r.data?.stage === 'furnished' ? 'is_furnished_reference' : 'is_whitebox_reference';
      toast.success(on
        ? (flag === 'is_textured_reference' ? 'Rendu texturé défini comme référence du lot (étape 3).'
          : flag === 'is_furnished_reference' ? 'Rendu meublé défini comme référence du lot (étape 4).'
          : 'Maquette blanche définie comme référence du lot (étape 2).')
        : 'Référence du lot retirée.');
      setItems((prev) => prev.map((it) => it.lot_id === item.lot_id
        ? { ...it, [flag]: on && it.job_id === item.job_id }
        : it));
      emitRefsChanged({ lotId: item.lot_id, stage: r.data?.stage || item.params?.render_stage, anchored: on });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la mise en référence.');
    }
  }, [projectId]);

  // V14.92 — Notation / refus / annotation : patch local (galerie + focus).
  const handleReviewChanged = useCallback((jobId, patch) => {
    setItems((prev) => prev.map((it) => (it.job_id === jobId ? { ...it, ...patch } : it)));
    setActive((cur) => (cur && cur.job_id === jobId ? { ...cur, ...patch } : cur));
  }, []);

  const handleFidelity = useCallback(async (item) => {
    const tid = toast.loading('Calcul de la fidélité au plan…');
    try {
      const r = await axios.post(`${API}/projects/${projectId}/generations/${item.job_id}/fidelity`, {}, opts);
      const patch = { fidelity_score: r.data.fidelity_score, fidelity_report: { scores: r.data.scores, issues: r.data.issues, summary: r.data.summary } };
      setItems((prev) => prev.map((it) => (it.job_id === item.job_id ? { ...it, ...patch } : it)));
      setActive((cur) => (cur && cur.job_id === item.job_id ? { ...cur, ...patch } : cur));
      toast.success(`Fidélité au plan : ${r.data.fidelity_score}%`, { id: tid });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Score indisponible.', { id: tid });
    }
  }, [projectId]);

  const handleDeleteConfirmed = useCallback(async () => {
    if (!deleteItem) return;
    const jid = deleteItem.job_id;
    setDeleteItem(null);
    try {
      await axios.delete(`${API}/projects/${projectId}/generations/${jid}`, opts);
      toast.success('Rendu supprimé.');
      setItems((prev) => prev.filter((it) => it.job_id !== jid && it.parent_job_id !== jid));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la suppression.');
    }
  }, [deleteItem, projectId]);

  // Open the before/after comparator on a variation tile. Resolves the parent
  // from the current items list so we don't fire an extra API call.
  const handleCompare = useCallback((variation) => {
    if (!variation?.parent_job_id) return;
    const parent = items.find((it) => it.job_id === variation.parent_job_id);
    if (!parent || !parent.image_url) {
      toast.error('Rendu parent introuvable ou image indisponible.');
      return;
    }
    if (!variation.image_url) {
      toast.error('La variation est encore en cours de génération.');
      return;
    }
    setCompareItem({ parent, variation });
  }, [items]);

  // V14.5 D2 — Set cover. Toggles : if already cover, clears it.
  const handleSetCover = useCallback(async (item) => {
    if (!item.lot_id) {
      toast.error('Ce rendu n\'est rattaché à aucun lot');
      return;
    }
    const targetLot = lots.find((l) => l.lot_id === item.lot_id);
    const isAlreadyCover = targetLot?.cover_generation_id === item.job_id;
    const newId = isAlreadyCover ? null : item.job_id;
    try {
      await axios.post(`${API}/lots/${item.lot_id}/cover`, { generation_id: newId }, opts);
      setLots((prev) => prev.map((l) => l.lot_id === item.lot_id ? { ...l, cover_generation_id: newId } : l));
      toast.success(isAlreadyCover
        ? 'Couverture du lot retirée'
        : 'Rendu épinglé comme couverture du lot');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la mise à jour');
    }
  }, [lots]);

  const counts = useMemo(() => {
    const scoped = singleLot ? items.filter((i) => i.lot_id === lotId && (stageFilter === 'all' || ((i.params?.render_stage) || 'full') === stageFilter)) : items;
    const archived = scoped.filter((i) => i.is_archived).length;
    const live = scoped.filter((i) => !i.is_archived);
    const total = live.length;
    const mat = live.filter((i) => i.config_source === 'materialtheque').length;
    const pending = live.filter((i) => i.status === 'pending' || i.status === 'processing').length;
    const validated = live.filter((i) => i.is_validated).length;
    const variations = live.filter((i) => !!i.parent_job_id).length;
    // "À revoir" = rendu prêt non validé, hors variations en cours
    const review = live.filter((i) => i.status === 'completed' && !i.is_validated).length;
    return { total, mat, def: total - mat, pending, validated, variations, review, archived };
  }, [items, singleLot, lotId, stageFilter]);

  // Build the style-chip filter pills — only show styles that actually exist in the items.
  const styleChips = useMemo(() => {
    const counter = new Map(); // style_id -> { id, name, count }
    for (const it of items) {
      const sid = it.style_decoration_id;
      if (!sid) continue;
      if (!counter.has(sid)) counter.set(sid, { id: sid, name: it.style_name || sid, count: 0 });
      counter.get(sid).count += 1;
    }
    return Array.from(counter.values()).sort((a, b) => b.count - a.count);
  }, [items]);

  // Build the tree structure (building → floor → lot) from the items list.
  // Counts are computed at each level so the sidebar can display "Bâtiment A · 23".
  const tree = useMemo(() => {
    const buildings = new Map(); // building_id -> { id, name, floors: Map<floor_id, {id,name,lots:Map}>, count }
    const ORPHAN = '__orphan__';
    for (const it of items) {
      const bid = it.building_id || ORPHAN;
      const bname = it.building_name || (bid === ORPHAN ? 'Non rattaché' : bid);
      const fid = it.floor_id || ORPHAN;
      const fname = it.floor_name || (fid === ORPHAN ? 'Sans étage' : fid);
      const lid = it.lot_id || ORPHAN;
      const lname = it.lot_number ? `Lot ${it.lot_number}` : (lid === ORPHAN ? 'Sans lot' : lid);

      if (!buildings.has(bid)) buildings.set(bid, { id: bid, name: bname, count: 0, floors: new Map() });
      const b = buildings.get(bid);
      b.count++;
      if (!b.floors.has(fid)) b.floors.set(fid, { id: fid, name: fname, count: 0, lots: new Map() });
      const f = b.floors.get(fid);
      f.count++;
      if (!f.lots.has(lid)) f.lots.set(lid, { id: lid, name: lname, typology: it.typology, count: 0 });
      f.lots.get(lid).count++;
    }
    // Convert nested Maps → arrays sorted alphabetically.
    return Array.from(buildings.values())
      .sort((a, b) => a.name.localeCompare(b.name, 'fr'))
      .map((b) => ({
        ...b,
        floors: Array.from(b.floors.values())
          .sort((a, b) => a.name.localeCompare(b.name, 'fr', { numeric: true }))
          .map((f) => ({
            ...f,
            lots: Array.from(f.lots.values())
              .sort((a, b) => a.name.localeCompare(b.name, 'fr', { numeric: true })),
          })),
      }));
  }, [items]);

  // Auto-expand all nodes the first time the tree comes online, so the user
  // immediately sees the structure (no empty sidebar on first load).
  useEffect(() => {
    if (tree.length === 0) return;
    setExpandedBuildings((prev) => prev.size > 0 ? prev : new Set(tree.map((b) => b.id)));
    setExpandedFloors((prev) => {
      if (prev.size > 0) return prev;
      const all = new Set();
      for (const b of tree) for (const f of b.floors) all.add(`${b.id}::${f.id}`);
      return all;
    });
  }, [tree]);

  const filtered = useMemo(() => {
    // V15.10 — les archives sont un dossier à part : masquées partout ailleurs.
    let out = statusFilter === 'archived' ? items.filter((i) => i.is_archived) : items.filter((i) => !i.is_archived);
    if (singleLot) out = out.filter((i) => i.lot_id === lotId);
    if (singleLot && stageFilter !== 'all') out = out.filter((i) => ((i.params?.render_stage) || 'full') === stageFilter);
    if (filter !== 'all') out = out.filter((i) => i.config_source === filter);
    if (statusFilter === 'pending') {
      out = out.filter((i) => i.status === 'pending' || i.status === 'processing');
    } else if (statusFilter === 'review') {
      out = out.filter((i) => i.status === 'completed' && !i.is_validated);
    } else if (statusFilter === 'validated') {
      out = out.filter((i) => i.is_validated);
    } else if (statusFilter === 'variations') {
      out = out.filter((i) => !!i.parent_job_id);
    }
    if (styleFilter !== 'all') {
      out = styleFilter === '__none__'
        ? out.filter((i) => !i.style_decoration_id)
        : out.filter((i) => i.style_decoration_id === styleFilter);
    }
    // Tree filter: building, floor or lot scope
    if (treeNode.type === 'building') {
      out = out.filter((i) => (i.building_id || '__orphan__') === treeNode.id);
    } else if (treeNode.type === 'floor') {
      out = out.filter((i) => (i.floor_id || '__orphan__') === treeNode.id);
    } else if (treeNode.type === 'lot') {
      out = out.filter((i) => (i.lot_id || '__orphan__') === treeNode.id);
    }
    return out;
  }, [items, filter, statusFilter, styleFilter, treeNode, singleLot, lotId, stageFilter]);

  // Group variations directly beneath their parent in the displayed grid order.
  // Children whose parent is filtered out still surface, sorted right after
  // their own creation order (defensive).
  const ordered = useMemo(() => {
    const byId = new Map(filtered.map((it) => [it.job_id, it]));
    const seen = new Set();
    const out = [];
    for (const it of filtered) {
      if (it.parent_job_id) continue; // children handled by parent
      if (seen.has(it.job_id)) continue;
      out.push(it);
      seen.add(it.job_id);
      // append all children of this parent (newest first → reverse to keep visual lineage)
      const kids = filtered
        .filter((c) => c.parent_job_id === it.job_id)
        .sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
      for (const k of kids) { out.push(k); seen.add(k.job_id); }
    }
    // Orphan children whose parent isn't in the filtered set — append at the end
    for (const it of filtered) {
      if (it.parent_job_id && !seen.has(it.job_id) && !byId.has(it.parent_job_id)) {
        out.push(it); seen.add(it.job_id);
      }
    }
    return out;
  }, [filtered]);

  return (
    <HudCard variant="glass" className="hud-stagger" data-testid="project-generation-gallery">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <span className="hud-eyebrow hud-eyebrow-gold">{singleLot ? 'Historique du lot' : 'Galerie'}</span>
          <p
            className="mt-0.5 font-semibold tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 18 }}
          >
            {singleLot ? 'Rendus de ce lot' : 'Rendus du projet'}
          </p>
          <p className="text-[12px] mt-0.5" style={{ color: 'var(--c-text-2)' }}>
            {singleLot ? `${filtered.length} rendu${filtered.length > 1 ? 's' : ''} affiché${filtered.length > 1 ? 's' : ''} · cliquez une tuile pour la vue détaillée` : `${counts.total} rendu${counts.total > 1 ? 's' : ''} · ${counts.mat} avec Matériauthèque · ${counts.def} par défaut`}
          </p>
        </div>
        {!singleLot && <div className="flex items-center gap-1.5 flex-wrap">
          {[
            { id: 'all',            label: 'Tous',          icon: Layers, n: counts.total },
            { id: 'materialtheque', label: 'Matériauthèque', icon: ShieldCheck, n: counts.mat },
            { id: 'default',        label: 'Par défaut',     icon: Cpu, n: counts.def },
          ].map((f) => {
            const Icon = f.icon;
            const isActive = filter === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                data-testid={`gallery-filter-${f.id}`}
                className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11.5px] font-medium transition-colors"
                style={{
                  background: isActive ? 'var(--c-surface-3)' : 'transparent',
                  color: isActive ? 'var(--c-text)' : 'var(--c-text-2)',
                  border: `1px solid ${isActive ? 'var(--c-border-accent)' : 'var(--c-border)'}`,
                }}
              >
                <Icon
                  className="w-3 h-3"
                  strokeWidth={1.75}
                  style={{ color: isActive ? 'var(--c-gold)' : 'var(--c-text-muted)' }}
                />
                {f.label}
                <span
                  className="ml-0.5 text-[10px] tabular-nums"
                  style={{ color: 'var(--c-text-muted)' }}
                >
                  {f.n}
                </span>
              </button>
            );
          })}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="gallery-refresh"
            className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11.5px] font-medium transition-colors disabled:opacity-40"
            style={{
              background: 'transparent',
              color: 'var(--c-text-2)',
              border: '1px solid var(--c-border)',
            }}
            aria-label="Recharger la galerie"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.75} />
          </button>
          {/* V14.90 — Parcours guidé 4 étapes */}
          <button
            type="button"
            onClick={() => setWizardOpen(true)}
            data-testid="gallery-stage-wizard"
            title="Assistant pas à pas : blanche → texturer → meubler → lumière, avec ancrage automatique du rendu validé"
            className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11.5px] font-semibold transition-colors"
            style={{ background: 'rgba(217,160,91,0.14)', color: '#F5C77E', border: '1px solid rgba(217,160,91,0.4)' }}
          >
            <Wand2 className="w-3 h-3" strokeWidth={1.75} />
            Parcours guidé
          </button>
          {/* V14.88 — Comparateur d'étapes (blanche / texturée / complète) */}
          {(() => {
            const byLot = new Map();
            items.filter((i) => i.status === 'completed' && i.image_url).forEach((i) => {
              byLot.set(i.lot_id, (byLot.get(i.lot_id) || new Set()).add(stageOf(i)));
            });
            const enabled = Array.from(byLot.values()).some((s) => s.size >= 2);
            return (
              <button
                type="button"
                onClick={() => setCompareOpen(true)}
                disabled={!enabled}
                data-testid="gallery-compare-stages"
                title={enabled ? 'Comparer maquette blanche / texturée / complète d\'un lot' : 'Disponible dès qu\'un lot possède deux étapes rendues'}
                className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11.5px] font-medium transition-colors disabled:opacity-40"
                style={{ background: 'transparent', color: 'var(--c-text-2)', border: '1px solid var(--c-border)' }}
              >
                <Layers3 className="w-3 h-3" strokeWidth={1.75} style={{ color: 'var(--c-gold)' }} />
                Comparer les étapes
              </button>
            );
          })()}
          {/* V14.88 — Empilement 3D des rendus d'étages (maison uniquement) */}
          <MaisonStackButton projectId={projectId} refreshNonce={refreshNonce} />
        </div>}
      </div>
      {compareOpen && (
        <StageCompareDialog open onClose={() => setCompareOpen(false)} items={items} lots={lots} />
      )}
      {wizardOpen && (
        <StageWizard open onClose={() => setWizardOpen(false)} projectId={projectId} items={items} lots={lots} onChanged={() => load(true)}
                     onOpenLauncher={(stage, lotId) => {
                       setWizardOpen(false);
                       emitRefsChanged({ lotId, stage: null, nextStage: stage });
                       setTimeout(() => document.querySelector('[data-testid="project-gen-stage-tabs"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
                     }} />
      )}

      {/* V14.83 — Résumé file d'attente */}
      {queueSummary && (
        <div
          className="flex items-center gap-3 flex-wrap mb-3 px-3 py-2 rounded-lg text-[11.5px]"
          style={{ background: 'rgba(147,197,253,0.07)', border: '1px solid rgba(147,197,253,0.25)', color: '#BFDBFE' }}
          data-testid="gallery-queue-summary"
        >
          <Hourglass className="w-3.5 h-3.5 animate-pulse" />
          <span>
            <strong>{queueSummary.processing}</strong> en cours · <strong>{queueSummary.pending}</strong> en attente
            {queueSummary.totalGlobal > queueSummary.pending && <span style={{ opacity: 0.75 }}> ({queueSummary.totalGlobal} au total sur la plateforme)</span>}
          </span>
          <span style={{ opacity: 0.6 }}>|</span>
          <span>Prochain rendu ~{formatEta(queueSummary.next)} · dernier ~{formatEta(queueSummary.last)}</span>
          <span style={{ opacity: 0.6 }}>|</span>
          <span style={{ opacity: 0.8 }}>{queueSummary.concurrency} générations en parallèle · actualisation auto</span>
        </div>
      )}

      {/* V14.5 D2 — Status filter chips (En attente / À revoir / Validés / Variations) */}
      <div
        className="flex items-center gap-1.5 flex-wrap mb-3"
        data-testid="gallery-status-filters"
      >
        <span className="hud-eyebrow" style={{ marginRight: 4 }}>Statut</span>
        {[
          { id: 'all',         label: 'Tous',        icon: Layers,        n: counts.total,      tone: 'neutral' },
          { id: 'pending',     label: 'En attente',  icon: Hourglass,     n: counts.pending,    tone: 'info'    },
          { id: 'review',      label: 'À revoir',    icon: Eye,           n: counts.review,     tone: 'warning' },
          { id: 'validated',   label: 'Validés',     icon: CheckCircle2,  n: counts.validated,  tone: 'success' },
          { id: 'variations',  label: 'Variations',  icon: GitBranch,     n: counts.variations, tone: 'gold'    },
          { id: 'archived',    label: 'Archives',    icon: Archive,       n: counts.archived,   tone: 'muted'   },
        ].map((s) => {
          const Icon = s.icon;
          const isActive = statusFilter === s.id;
          const accentMap = {
            neutral: 'var(--c-text)',
            info:    '#93C5FD',
            warning: '#FDE68A',
            success: '#86EFAC',
            gold:    'var(--c-gold)',
            muted:   '#CBD5E1',
          };
          const accent = accentMap[s.tone];
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setStatusFilter(s.id)}
              data-testid={`gallery-status-filter-${s.id}`}
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors"
              style={{
                background: isActive ? `${accent}1A` : 'transparent',
                color: isActive ? accent : 'var(--c-text-2)',
                border: `1px solid ${isActive ? `${accent}55` : 'var(--c-border)'}`,
              }}
            >
              <Icon className="w-3 h-3" strokeWidth={1.75} />
              {s.label}
              <span className="text-[9.5px] tabular-nums" style={{ color: 'var(--c-text-muted)' }}>
                {s.n}
              </span>
            </button>
          );
        })}
      </div>

      {/* Style chip filters — only displayed when at least one rendu carries a style */}
      {!singleLot && styleChips.length > 0 && (
        <div
          className="flex items-center gap-1.5 flex-wrap mb-4 pb-1"
          data-testid="gallery-style-filters"
        >
          <span className="hud-eyebrow" style={{ marginRight: 4 }}>Styles</span>
          {[
            { id: 'all',      name: 'Tous',          count: items.length, gold: false },
            ...styleChips.map((s) => ({ ...s, gold: true })),
            {
              id: '__none__',
              name: 'Sans style',
              count: items.filter((i) => !i.style_decoration_id).length,
              gold: false,
            },
          ]
            .filter((c) => c.id === 'all' || c.count > 0)
            .map((s) => {
              const active = styleFilter === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setStyleFilter(s.id)}
                  data-testid={`gallery-style-filter-${s.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10.5px] font-medium transition-colors"
                  style={{
                    background: active
                      ? (s.gold ? 'rgba(217,160,91,0.22)' : 'var(--c-surface-3)')
                      : 'transparent',
                    color: active
                      ? (s.gold ? 'var(--c-gold)' : 'var(--c-text)')
                      : 'var(--c-text-2)',
                    border: `1px solid ${active
                      ? (s.gold ? 'var(--c-border-accent)' : 'var(--c-border)')
                      : 'var(--c-border)'}`,
                    maxWidth: 180,
                  }}
                >
                  {s.gold && <Sparkles className="w-2.5 h-2.5 shrink-0" />}
                  <span className="truncate">{s.name}</span>
                  <span className="text-[9px] tabular-nums" style={{ color: 'var(--c-text-muted)' }}>
                    {s.count}
                  </span>
                </button>
              );
            })}
        </div>
      )}

      {/* Body — sidebar tree (Bâtiment → Étage → Lot) + grid OR Mode Focus */}
      {active ? (
        // V14.5 D2 — Mode Focus : prend tout l'espace, masque la grille + tree.
        // V15.22 — en mode lot unique (cockpit), la vue focus s'ouvre en plein écran (portal) par-dessus la page.
        (singleLot ? (n) => createPortal(n, document.body) : (n) => n)(
        <div className={singleLot ? 'fixed inset-0 z-[70] overflow-y-auto p-4 md:p-6' : ''}
             style={singleLot ? { background: 'rgba(8,9,12,0.94)', backdropFilter: 'blur(10px)' } : undefined}
             data-testid={singleLot ? 'single-lot-focus-overlay' : undefined}
             onKeyDown={(e) => { if (singleLot && e.key === 'Escape') setActive(null); }}>
        <div className={singleLot ? 'max-w-[1600px] mx-auto rounded-2xl p-4' : ''} style={singleLot ? { background: 'var(--c-surface-0)', border: '1px solid var(--c-border)' } : undefined}>
        <GalleryFocusView
          projectId={projectId}
          items={ordered}
          activeId={active.job_id}
          onChange={(jid) => setActive(ordered.find((it) => it.job_id === jid) || null)}
          onClose={() => setActive(null)}
          onShare={setShareItem}
          onValidate={(it) => {
            handleValidate(it);
            setActive((cur) => cur ? { ...cur, is_validated: !cur.is_validated } : cur);
          }}
          onVariation={(it) => { setActive(null); setVariationItem(it); }}
          onUpscale={(it) => setUpscaleItem(it)}
          onCompare={(it) => { setActive(null); handleCompare(it); }}
          onDelete={(it) => { setActive(null); setDeleteItem(it); }}
          onArchive={(it) => {
            handleArchive(it);
            setActive((cur) => cur ? { ...cur, is_archived: !cur.is_archived } : cur);
          }}
          onSetCover={handleSetCover}
          onFidelity={handleFidelity}
          onReviewChanged={handleReviewChanged}
          onRelaunched={() => load(true)}
          onWhiteboxRef={(it) => {
            handleWhiteboxRef(it);
            const flag = it.params?.render_stage === 'textured' ? 'is_textured_reference' : it.params?.render_stage === 'furnished' ? 'is_furnished_reference' : 'is_whitebox_reference';
            setActive((cur) => cur ? { ...cur, [flag]: !cur[flag] } : cur);
          }}
          isCoverFor={(() => {
            const lot = lots.find((l) => l.lot_id === active.lot_id);
            return lot?.cover_generation_id || null;
          })()}
          onAnnotate={(it) => setAnnotateItem(it)}
        />
        </div>
        </div>)
      ) : (
      <div className="relative flex gap-4">
        {!singleLot && tree.length > 0 && (
          <GalleryTree
            tree={tree}
            totalCount={items.length}
            selected={treeNode}
            onSelect={setTreeNode}
            expandedBuildings={expandedBuildings}
            setExpandedBuildings={setExpandedBuildings}
            expandedFloors={expandedFloors}
            setExpandedFloors={setExpandedFloors}
          />
        )}
        <div className="flex-1 min-w-0">
          {loading ? (
            <SkeletonGrid />
          ) : error ? (
            <div
              className="rounded-xl border border-dashed flex items-center justify-center py-10 text-[12.5px]"
              style={{ borderColor: 'rgba(239,68,68,0.35)', color: '#FCA5A5' }}
              data-testid="gallery-error"
            >
              {error}
            </div>
          ) : filtered.length === 0 ? (
            items.length === 0 ? (
              <EmptyState projectId={projectId} />
            ) : (
              <div
                className="rounded-xl border border-dashed flex items-center justify-center py-10 text-[12.5px]"
                style={{ borderColor: 'rgba(255,255,255,0.10)', color: 'var(--c-text-faint)' }}
                data-testid="gallery-no-match"
              >
                Aucun rendu ne correspond à ce filtre.
              </div>
            )
          ) : (
            <div
              className={compact ? 'grid grid-cols-2 gap-3' : 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3'}
              data-testid={singleLot ? 'single-lot-gallery-grid' : 'gallery-grid'}
            >
              {ordered.map((item, idx) => (
                <GalleryTile
                  key={item.job_id}
                  item={item}
                  onOpen={setActive}
                  onShare={setShareItem}
                  onValidate={handleValidate}
                  onDelete={setDeleteItem}
                  onVariation={setVariationItem}
                  onCompare={handleCompare}
                  onWhiteboxRef={handleWhiteboxRef}
                  onArchive={handleArchive}
                  onUpscale={setUpscaleItem}
                  index={idx}
                  alwaysShowActions={singleLot}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {shareItem && (
        <RenderShareDialog
          projectId={projectId}
          item={shareItem}
          onClose={() => setShareItem(null)}
        />
      )}

      {variationItem && (
        <VariationDialog
          projectId={projectId}
          parentItem={variationItem}
          onClose={() => setVariationItem(null)}
          onCreated={() => { setVariationItem(null); load(); }}
        />
      )}

      {upscaleItem && (
        <UpscaleDialog
          projectId={projectId}
          item={upscaleItem}
          onClose={() => setUpscaleItem(null)}
          onChanged={handleUpscaleChanged}
        />
      )}

      {compareItem && (
        <BeforeAfterCompareDialog
          parent={compareItem.parent}
          variation={compareItem.variation}
          onClose={() => setCompareItem(null)}
        />
      )}

      {/* V14.5 D3 — Annotation & selective regeneration */}
      <AnnotationDialog
        open={!!annotateItem}
        onClose={() => setAnnotateItem(null)}
        projectId={projectId}
        generation={annotateItem}
        onSubmitted={() => {
          // Reload gallery so the new pending revision appears immediately
          load();
        }}
      />

      <AlertDialog open={!!deleteItem} onOpenChange={(open) => !open && setDeleteItem(null)}>
        <AlertDialogContent data-testid="gallery-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce rendu ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le rendu disparaîtra de la galerie. {deleteItem && !deleteItem.parent_job_id
                ? 'Toutes ses variations seront également supprimées.'
                : ''} Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="gallery-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirmed}
              data-testid="gallery-delete-confirm-action"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Local keyframes for shimmer (kept scoped) */}
      <style>{`
        @keyframes hud-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </HudCard>
  );
};

export default ProjectGenerationGallery;
